import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Theme } from "@mui/material";
import {
  Box,
  CircularProgress,
  Link,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { ActionModal, FormErrorMessage } from "@components";
import {
  BottomActionArea,
  BottomActionButton,
  CodeInput,
  FullScreenLoader,
  SnackBar,
  NavigateBeforeButton,
  ExtraWhiteSpace,
  FormHeader,
  FormContentContainer,
  FormBody,
} from "@components";
import type { RequestType } from "@interfaces";
import { apiGateway, queryClient } from "@contexts";
import {
  getFromLocalStorage,
  getUnitForQuantity,
  onContactUsClick,
  redirectToPinScreenResolver,
} from "@utils";
import { needHelpMessage } from "../../constants";
import { millisecondsInSecond } from "date-fns";
import type { ErrorType } from "./hooks/onQuestionChallengeError";
import { onQuestionChallengeAxiosErrorInterpreter } from "./hooks/onQuestionChallengeError";
import { useEnrolmentErrorHandler } from "./hooks/useEnrolmentErrorHandler";

type CreateCertificateStatus =
  | "NOT_STARTED"
  | "CREATING_CERTIFICATE_APPLICATION"
  | "DOWNLOADING_CERTIFICATE"
  | "SUCCESS"
  | "ERROR";

type CertificateCreationStatusPayload = {
  status: CreateCertificateStatus;
  // BPO kept the certificate of an earlier attempt, with that attempt's PIN.
  certificateStillValid?: boolean;
};

const STILL_VALID_NOTICE = {
  title: "Tu firma electrónica ya estaba creada",
  description:
    "Firma con el PIN que elegiste la primera vez que creaste tu firma. Si no lo recuerdas, usa «Olvidé mi PIN» cuando firmes.",
};

// The creation's own time limit on the server.
const CREATION_MAX_WAIT_MS = 240 * millisecondsInSecond;

const isGatewayTimeout = (error: Error) => {
  const { isAxiosError, response } = error as AxiosError;

  return isAxiosError === true && (!response || response.status === 504);
};

const PinContainer = styled(Box)({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  marginTop: "64px",
});

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const PinLabel = styled(Typography)({
  textAlign: "center",
  padding: "16px",
});

const PinErrorContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ContactUsContainer = styled(Box)({
  paddingTop: "20px",
});

const Bold = styled("span")({
  fontWeight: 600,
});

const PinDisclaimerContainer = styled(Box)({
  display: "flex",
  padding: "32px 0px 16px 0px",
});

const PinDisclaimer = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const TitleScreenLoader = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 500,
  textAlign: "center",
});

const SubtitleLoader = styled(Typography)({
  textAlign: "center",
});

export function CreatePinScreen() {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const retryCreateCertificateRef = useRef<(() => void) | null>(null);

  const { handleEnrolmentError, enrolmentErrorModals } = useEnrolmentErrorHandler({
    onRetry: () => retryCreateCertificateRef.current?.(),
    fallbackTitle: "Error al crear tu firma",
  });

  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const [pin, setPin] = useState("");
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30);
  const [isCreatingMagicLink, setIsCreatingMagicLink] = useState(false);
  const [showStillValidNotice, setShowStillValidNotice] = useState(false);

  const requestType = getFromLocalStorage<RequestType>({
    key: "requestType",
  }) as RequestType;

  // Set once the status says the certificate exists, so a request error that
  // arrives later changes nothing.
  const certificateCreatedRef = useRef(false);
  const [timedOutError, setTimedOutError] = useState<Error | null>(null);

  const { mutate: createCertificate, isPending: creatingCertIsPending } = useMutation({
    mutationFn: (pin: string) => {
      certificateCreatedRef.current = false;
      setTimedOutError(null);
      setIsPollingStatus(true);

      return apiGateway<unknown, ErrorType>(
        {
          method: "post",
          url: "/enrolment/certificate",
          data: { pin },
        },
        onQuestionChallengeAxiosErrorInterpreter,
      );
    },
    onError: async (error: Error) => {
      if (certificateCreatedRef.current) return;
      // The gateway gives up before the creation does: the status decides,
      // so a certificate created afterwards, and its notice, are not lost.
      if (isGatewayTimeout(error)) {
        setTimedOutError(error);

        return;
      }

      setIsPollingStatus(false);
      await handleEnrolmentError(error);
    },
  });

  // The error modal's retry creates the certificate with the latest PIN; set after
  // render, not during it.
  useEffect(() => {
    retryCreateCertificateRef.current = () => createCertificate(pin);
  }, [createCertificate, pin]);

  const { data: certificateCreationStatus } = useQuery({
    queryKey: ["certificateCreationStatus"],
    queryFn: () =>
      apiGateway<CertificateCreationStatusPayload>({
        method: "get",
        url: "/enrolment/certificate/status",
      }),
    enabled: isPollingStatus,
    refetchInterval: 3 * millisecondsInSecond,
    initialData: { status: "NOT_STARTED" },
  });

  const { isCertificateReadyToUse, isCertificateFailed } = useMemo(() => {
    if (!certificateCreationStatus)
      return { isCertificateReadyToUse: false, isCertificateFailed: false };
    const { status } = certificateCreationStatus;

    return {
      isCertificateReadyToUse: status === "SUCCESS",
      isCertificateFailed: status === "ERROR",
    };
  }, [certificateCreationStatus]);

  const isCertificateStillValid =
    certificateCreationStatus?.certificateStillValid === true;

  const leaveEnrolment = useCallback(() => {
    navigate(redirectToPinScreenResolver(requestId, requestType));
  }, [navigate, requestId, requestType]);

  // A creation that ends failed or with a certificate still valid stops the polling; a
  // still-valid certificate also shows its notice. Adjusted during render, when the status
  // arrives.
  if (isPollingStatus && timedOutError && isCertificateFailed) {
    setIsPollingStatus(false);
  }

  if (isCertificateReadyToUse && isCertificateStillValid && !showStillValidNotice) {
    setIsPollingStatus(false);
    setShowStillValidNotice(true);
  }

  useEffect(() => {
    if (!timedOutError || isCertificateFailed) return;

    // The creation never ended: handled as the error it was.
    const timer = setTimeout(() => {
      if (certificateCreatedRef.current) return;
      setIsPollingStatus(false);
      setTimedOutError(null);
      handleEnrolmentError(timedOutError);
    }, CREATION_MAX_WAIT_MS);

    return () => clearTimeout(timer);
  }, [timedOutError, isCertificateFailed, handleEnrolmentError]);

  useEffect(() => {
    if (!isCertificateReadyToUse) return;
    certificateCreatedRef.current = true;
    queryClient.invalidateQueries({ queryKey: ["doctor"] });
    // The PIN just typed may not be the one that signs: the notice says so before leaving.
    if (isCertificateStillValid) return;

    leaveEnrolment();
  }, [isCertificateReadyToUse, isCertificateStillValid, leaveEnrolment]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleNextScreen = useCallback(async () => {
    if (pin.length === 4) {
      setIsCreatingMagicLink(true);
      await apiGateway({ method: "POST", url: "/auth/create-magic-link" });
      setIsCreatingMagicLink(false);
      createCertificate(pin);
    }
  }, [pin, createCertificate]);

  const handlePinChange = (val: string) => {
    setPin(val);
  };

  // The certificate exists once the notice shows, even if the request is still
  // on its way back.
  const isWaitingForCertificateCreation =
    !showStillValidNotice &&
    (isCreatingMagicLink ||
      creatingCertIsPending ||
      (isPollingStatus &&
        !["SUCCESS", "ERROR"].includes(certificateCreationStatus.status)));

  useEffect(() => {
    function updateRemainingSeconds() {
      let timerId: ReturnType<typeof setInterval> | null = null;

      if (isWaitingForCertificateCreation && remainingSeconds > 0) {
        timerId = setInterval(() => {
          setRemainingSeconds((prev) => Math.max(prev - 1, 0));
        }, 1000);
      }

      return () => {
        if (timerId) clearInterval(timerId);
      };
    }

    return updateRemainingSeconds();
  }, [isWaitingForCertificateCreation, remainingSeconds]);

  if (isWaitingForCertificateCreation)
    return (
      <>
        <FormHeader
          title={!isDesktop ? "Verificación de identidad" : undefined}
          useMekiDocIcon={isDesktop}
          titleColor="white"
          left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
        />
        <FormContentContainer disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader>
              <TitleScreenLoader>
                {remainingSeconds
                  ? "Estamos registrando tu firma electrónica"
                  : "Estamos demorando un poco más de lo esperado."}
              </TitleScreenLoader>
              <SubtitleLoader>
                {remainingSeconds
                  ? `Este proceso puede durar ${remainingSeconds} ${getUnitForQuantity(
                      "segundo",
                      remainingSeconds,
                    )}`
                  : "Danos unos segundos más para finalizar el registro."}
              </SubtitleLoader>
            </FullScreenLoader>
          </FormBody>
        </FormContentContainer>
      </>
    );

  return (
    <>
      <FormHeader
        title={!isDesktop ? "Verificación de identidad" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Title variant="h5" fontWeight={"medium"}>
            Verificación de identidad
          </Title>
          <PinContainer>
            <PinLabel>
              Crea un <Bold>PIN de 4 dígitos</Bold>. Te lo pediremos cada vez que quieras
              firmar una receta
            </PinLabel>
            <CodeInput
              code={pin}
              handleCodeChange={handlePinChange}
              numInputs={4}
              isValid={!isCertificateFailed}
              type={"number"}
            />
            {isCertificateFailed && (
              <PinErrorContainer>
                <FormErrorMessage>
                  Ha ocurrido un error con la creación de tu PIN
                </FormErrorMessage>
                <ContactUsContainer>
                  Si el problema persiste{" "}
                  <Link
                    onClick={() => {
                      onContactUsClick(needHelpMessage);
                    }}
                  >
                    contáctanos
                  </Link>
                </ContactUsContainer>
              </PinErrorContainer>
            )}
            <PinDisclaimerContainer>
              <PinDisclaimer>
                <Bold>Elige un PIN que te sea fácil de recordar.</Bold> En caso de
                olvidarlo tendrás que volver a verificar tu identidad
              </PinDisclaimer>
            </PinDisclaimerContainer>
          </PinContainer>

          <ExtraWhiteSpace />

          <SnackBar
            snackBarState={{
              open: isCertificateReadyToUse,
              type: "success",
            }}
            alerts={{
              success: "Registro exitoso",
            }}
          />
          <BottomActionArea>
            <BottomActionButton
              onClick={handleNextScreen}
              disabled={pin.length !== 4 || isWaitingForCertificateCreation}
            >
              {isWaitingForCertificateCreation ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Siguiente"
              )}
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
      {enrolmentErrorModals}
      <ActionModal
        isOpen={showStillValidNotice}
        onClose={leaveEnrolment}
        title={STILL_VALID_NOTICE.title}
        description={STILL_VALID_NOTICE.description}
        primaryButton={{ label: "Entendido", onClick: leaveEnrolment }}
      />
    </>
  );
}
